/**
 * useStrategyExecution Hook - 전략 실행 로직 및 상태 관리
 */
import { useState, useEffect, useCallback } from 'react';
import { getIntervals } from '../services/configApi';
import { getSecondsUntilNextRefresh, getSecondsUntilNextSlot } from '../services/watchlistApi';
import type { ExecutionLog } from '../types/strategy';
import type { AIStockData } from '../types/watchlist';
import {
  fetchSignalsForStocks,
  fetchWatchlistData,
  handleRefresh,
  isRateLimitError,
  executeStrategyForSymbol,
  fetchExecutionLogs,
} from './strategyExecutionHelpers';

type Options = { enableAiWatchlist?: boolean };

export function useStrategyExecution(options: Options = {}) {
  const enableAiWatchlist = options.enableAiWatchlist ?? true;
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [executingSymbol, setExecutingSymbol] = useState<string | null>(null);
  const [aiStocks, setAiStocks] = useState<AIStockData[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const [lastRefreshTime, setLastRefreshTime] = useState<string | null>(null);
  const [refreshIntervalSeconds, setRefreshIntervalSeconds] = useState<number>(1800);
  const [nextRefreshSlotEt, setNextRefreshSlotEt] = useState<string | null>(null);
  const [uiRefreshInterval, setUiRefreshInterval] = useState<number>(300000);

  const fetchLogs = useCallback(async () => {
    try {
      setLogs(await fetchExecutionLogs(50));
    } catch (error) {
      console.error('로그 조회 실패:', error);
    }
  }, []);

  const fetchAIWatchlist = useCallback(async () => {
    if (!enableAiWatchlist) {
      setAiStocks([]);
      setAiLoading(false);
      setAiError(null);
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      const { recommendations, lastRefresh, refreshInterval, nextSlotEt } = await fetchWatchlistData();
      setAiStocks(recommendations.map((stock) => ({ ...stock, signal_status: 'loading' })));
      setAiLoading(false);
      if (lastRefresh) setLastRefreshTime(lastRefresh);
      setNextRefreshSlotEt(nextSlotEt || null);
      if (refreshInterval !== undefined) setRefreshIntervalSeconds(refreshInterval);
      fetchSignalsForStocks(recommendations).then(setAiStocks);
    } catch (error: any) {
      console.error('AI Watchlist 조회 실패:', error);
      setAiError(error.message);
      setAiLoading(false);
    }
  }, [enableAiWatchlist]);

  const handleManualRefresh = async () => {
    if (!enableAiWatchlist) return;
    if (!window.confirm('AI Watchlist를 수동으로 새로고침하시겠습니까?')) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const { count, refreshedAt } = await handleRefresh();
      alert(`Watchlist 새로고침 완료\n\n종목 수: ${count}개\n새로고침 시간: ${new Date(refreshedAt).toLocaleString('ko-KR')}`);
      await fetchAIWatchlist();
    } catch (error: any) {
      const errorMsg = error.message || String(error);
      if (isRateLimitError(errorMsg)) {
        setAiError('Gemini API 할당량 초과 (429 Rate Limit)');
        alert('Gemini API 할당량 초과\n\n• 기존 Watchlist는 계속 사용 가능합니다\n• 24시간 후 자동 재개됩니다');
      } else {
        setAiError(errorMsg);
        alert(`새로고침 실패: ${errorMsg}`);
      }
    } finally {
      setAiLoading(false);
    }
  };

  const handleManualExecute = async (symbol: string) => {
    if (!window.confirm(`${symbol}에 대해 전략을 실행하시겠습니까?`)) return;
    setExecutingSymbol(symbol);
    try {
      const result = await executeStrategyForSymbol(symbol);
      alert(`${symbol} 실행 완료\n\n액션: ${result.action}\n실행 여부: ${result.executed ? '실행됨' : '미실행'}\n사유: ${result.reason}`);
      await fetchLogs();
    } catch (error: any) {
      alert(`실행 실패: ${error.response?.data?.detail || error.message}`);
    } finally {
      setExecutingSymbol(null);
    }
  };

  // 초기 로드
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      for (let retry = 0; retry < 3; retry++) {
        try {
          const intervals = await getIntervals();
          setUiRefreshInterval(intervals.ui_refresh_interval * 1000);
          await Promise.all([
            fetchLogs(),
            enableAiWatchlist ? fetchAIWatchlist() : Promise.resolve(),
          ]);
          setError(null);
          break;
        } catch (err: any) {
          if (retry >= 2) setError(`초기화 실패: ${err.message}. 페이지를 새로고침하세요.`);
          else await new Promise(r => setTimeout(r, 1000));
        }
      }
      setLoading(false);
    };
    initialize();
  }, [enableAiWatchlist, fetchAIWatchlist, fetchLogs]);

  // 주기적 로그 갱신 (30초)
  useEffect(() => {
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, [fetchLogs]);

  // 전략 신호 자동 업데이트
  useEffect(() => {
    if (!enableAiWatchlist || aiStocks.length === 0) return;
    const interval = setInterval(async () => {
      setAiStocks(await fetchSignalsForStocks(aiStocks));
    }, uiRefreshInterval);
    return () => clearInterval(interval);
  }, [aiStocks, enableAiWatchlist, uiRefreshInterval]);

  // 카운트다운 타이머
  useEffect(() => {
    if (!enableAiWatchlist) {
      setCountdown(0);
      return;
    }
    const updateCountdown = () => {
      const seconds = nextRefreshSlotEt
        ? getSecondsUntilNextSlot(nextRefreshSlotEt)
        : getSecondsUntilNextRefresh(lastRefreshTime, refreshIntervalSeconds);
      setCountdown(seconds);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [enableAiWatchlist, lastRefreshTime, refreshIntervalSeconds, nextRefreshSlotEt]);

  return {
    logs, loading, error, executingSymbol, aiStocks, aiLoading, aiError, countdown, uiRefreshInterval,
    handleManualRefresh, handleManualExecute,
  };
}
